import { z } from "zod";
import { EntityBase, Name, Note } from "./common.js";
import { Id } from "./ids.js";
import { MobilePhone } from "./phone.js";

/**
 * Customer contracts (glossary: Organization, OrderingUnit, Contact).
 * Organizations form a multi-level tree; orders are placed by ordering units (leaves).
 * An organization without children is still valid and gets one ordering unit (DL-025).
 */

export const Organization = EntityBase.extend({
  parentId: Id.nullable(),
  name: Name,
  /** Optional short code used on slips and in Excel mapping. */
  code: z.string().trim().max(24).nullable(),
});
export type Organization = z.infer<typeof Organization>;

export const OrderingUnit = EntityBase.extend({
  organizationId: Id,
  name: Name,
  code: z.string().trim().max(24).nullable(),
});
export type OrderingUnit = z.infer<typeof OrderingUnit>;

/** A unit's ordering contact. The manager pre-registers the phone; the customer door then links it (ADR-29). */
export const Contact = EntityBase.extend({
  orderingUnitId: Id,
  /** Filled once the phone has signed in through the customer door. */
  personId: Id.nullable(),
  name: Name,
  phone: MobilePhone,
  isPrimary: z.boolean(),
});
export type Contact = z.infer<typeof Contact>;

/** A subscriber asking to become the contact of a unit; the manager decides. */
export const UnitLinkRequest = EntityBase.extend({
  personId: Id,
  orderingUnitId: Id,
  note: Note,
  status: z.enum(["pending", "approved", "rejected"]),
  decidedById: Id.nullable(),
});
export type UnitLinkRequest = z.infer<typeof UnitLinkRequest>;
